import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const CONFIG_LABELS = {
  tax_rate_percent: { label: 'VAT Rate (%)', desc: 'Kenya standard 16%', type: 'number' },
  travel_rate_per_km_kes: { label: 'Travel Rate per Km (KES)', desc: 'Default if no rate card', type: 'number' },
  invoice_due_days: { label: 'Invoice Due Days', desc: 'Days until invoice is overdue', type: 'number' },
  mpesa_business_shortcode: { label: 'M-Pesa Shortcode', desc: 'Daraja paybill/till number', type: 'text' },
  mpesa_consumer_key: { label: 'M-Pesa Consumer Key', desc: 'Daraja API key', type: 'password' },
  mpesa_consumer_secret: { label: 'M-Pesa Consumer Secret', desc: 'Daraja API secret', type: 'password' },
  mpesa_passkey: { label: 'M-Pesa Passkey', desc: 'STK push passkey', type: 'password' },
  mpesa_callback_url: { label: 'M-Pesa Callback URL', desc: 'Public HTTPS URL for STK callbacks', type: 'text' },
  stripe_secret_key: { label: 'Stripe Secret Key', desc: 'sk_live_... or sk_test_...', type: 'password' },
  stripe_publishable_key: { label: 'Stripe Publishable Key', desc: 'pk_live_... or pk_test_...', type: 'text' },
  stripe_webhook_secret: { label: 'Stripe Webhook Secret', desc: 'whsec_... (from Stripe → Webhooks)', type: 'password' },
  stripe_currency: { label: 'Stripe Currency', desc: 'ISO 4217 lowercase (e.g. kes)', type: 'text' },
  frontend_url: { label: 'Frontend URL', desc: 'Base URL of this MIS for Stripe redirect (e.g. http://localhost:3000)', type: 'text' },
};

export default function BillingConfig() {
  const [config, setConfig] = useState({});
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => {
    api.get('/billing/config/settings').then(res => {
      const raw = res.data.data || {};
      const flat = {};
      Object.entries(raw).forEach(([k, v]) => { flat[k] = v.value || ''; });
      setConfig(flat);
      setEdits(flat);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/billing/config/settings', edits);
      setConfig({ ...edits });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const GROUPS = [
    { title: 'General', keys: ['tax_rate_percent', 'travel_rate_per_km_kes', 'invoice_due_days'] },
    { title: 'M-Pesa (Daraja API)', keys: ['mpesa_business_shortcode', 'mpesa_consumer_key', 'mpesa_consumer_secret', 'mpesa_passkey', 'mpesa_callback_url'] },
    { title: 'Stripe', keys: ['stripe_secret_key', 'stripe_publishable_key', 'stripe_webhook_secret', 'stripe_currency', 'frontend_url'] },
  ];

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#2b1b22' }}>Billing Configuration</h1>
          <p style={{ margin: '4px 0 0', color: '#8b7681', fontSize: 13 }}>Payment gateway credentials and billing defaults</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {saved && <span style={{ color: '#27ae60', fontWeight: 700, fontSize: 13 }}>✓ Saved</span>}
          <button onClick={save} disabled={saving}
            style={{ background: '#7b1e3c', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save All Settings'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 20 }}>
        {GROUPS.map(group => (
          <div key={group.title} style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12, padding: 24 }}>
            <h3 style={{ margin: '0 0 18px', fontSize: 14, fontWeight: 800, color: '#2b1b22' }}>{group.title}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
              {group.keys.map(key => {
                const meta = CONFIG_LABELS[key] || { label: key, desc: '', type: 'text' };
                const isPass = meta.type === 'password';
                return (
                  <div key={key}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#2b1b22' }}>{meta.label}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={isPass && !showPasswords[key] ? 'password' : 'text'}
                        value={edits[key] || ''}
                        onChange={e => setEdits(prev => ({ ...prev, [key]: e.target.value }))}
                        style={{ width: '100%', padding: `9px ${isPass ? 36 : 12}px 9px 12px`, borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }}
                      />
                      {isPass && (
                        <button onClick={() => setShowPasswords(p => ({ ...p, [key]: !p[key] }))}
                          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#8b7681' }}>
                          {showPasswords[key] ? '🙈' : '👁️'}
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#8b7681', marginTop: 3 }}>{meta.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
